import { createTrackingConfig } from '../../components/generic/labels';
import { getDateFormat, tableConfig, uiConfig } from '../../config/appConfig';

export const contractConfig = {
  entityName: 'Contract',
  /** Matches backend `EntityType` id for contract file attachments (see seed / migration). */
  entityTypeId: 18,

  api: {
    baseEndpoint: '/contract',
    endpoints: {
      list: '',
      create: '',
      update: (id) => `/${id}`,
      delete: (id) => `/${id}`,
      getById: (id) => `/${id}`,
      bulkDelete: '/bulk-delete',
    },
    lookupEndpoints: {
      suppliers: '/supplier',
      contractCategories: '/contract-category',
      userDepartments: '/user-department',
    },
    swrInvalidation: {
      mainList: '/contract',
      fileAttachments: (entityTypeId) => `/file-attachment/entity/${entityTypeId}/*`,
    },
  },

  ui: {
    drawerWidth: '35%',
    defaultPageSize: tableConfig.defaultPageSize,
    pageSizeOptions: tableConfig.pageSizeOptions,
    defaultTableDensity: tableConfig.defaultDensity,
    requiredFieldStyle: uiConfig.requiredFieldStyle,
    tableVariant: 'default',
    splitView: true,
    searchInputMaxWidth: uiConfig.searchInputMaxWidth,
  },

  features: {
    fileAttachments: true,
    masterEntityTypeId: 18,
  },

  displayName: 'Contract Management',
  description: 'Supplier contract lifecycle management with expiry tracking',
  icon: 'M19 5H5c-1.1 0-2 .9-2 2v10a2 2 0 002 2h14a2 2 0 002-2V7c0-1.1-.9-2-2-2zm-8 10H7v-2h4v2zm6-4H7V9h10v2z',

  fieldsToRemove: [
    'supplier',
    'category',
    'userDepartment',
    'creator',
    'updater',
    'contractStatus',
    'createdAt',
    'updatedAt',
    'createdBy',
    'updatedBy',
    'fileAttachments',
    'fileAttachmentCount',
  ],

  fields: [
    {
      name: 'id',
      label: 'ID',
      type: 'hidden',
      table: false,
      filterable: false,
    },
    {
      name: 'contractNumber',
      label: 'Contract Number',
      type: 'text',
      required: true,
      placeholder: 'Enter unique contract number',
      table: { header: 'Number', width: '160px', sortable: true },
      filterable: true,
      validation: {
        required: true,
        maxLength: 100,
        message: 'Contract number is required and must not exceed 100 characters',
      },
    },
    {
      name: 'contractTitle',
      label: 'Title',
      type: 'text',
      required: true,
      placeholder: 'Enter contract title',
      table: { header: 'Title', width: '260px', sortable: true },
      filterable: true,
      validation: {
        required: true,
        maxLength: 255,
        message: 'Contract title is required and must not exceed 255 characters',
      },
    },
    {
      name: 'description',
      label: 'Description',
      type: 'textarea',
      rows: 4,
      placeholder: 'Enter contract details',
      table: false,
      filterable: false,
      validation: {
        maxLength: 2000,
      },
    },
    {
      name: 'supplierId',
      label: 'Entity',
      type: 'select',
      required: true,
      lookupEndpoint: 'suppliers',
      options: [],
      table: {
        header: 'Entity',
        width: '180px',
        sortable: true,
        displayField: 'supplier',
        cellRenderer: 'badge',
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: {
        required: true,
        integer: true,
        message: 'Please select a valid entity',
      },
    },
    {
      name: 'categoryId',
      label: 'Category',
      type: 'select',
      required: true,
      lookupEndpoint: 'contractCategories',
      labelField: 'catName',
      options: [],
      table: {
        header: 'Category',
        width: '160px',
        sortable: true,
        displayField: 'category',
        cellRenderer: 'badge',
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: {
        required: true,
        integer: true,
        message: 'Please select a valid category',
      },
    },
    {
      name: 'departmentId',
      label: 'Department',
      type: 'select',
      required: true,
      lookupEndpoint: 'userDepartments',
      options: [],
      table: {
        header: 'Department',
        width: '170px',
        sortable: true,
        displayField: 'userDepartment',
        cellRenderer: 'badge',
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: {
        required: true,
        integer: true,
        min: 1,
        message: 'Department is required',
      },
    },
    {
      name: 'isPrivate',
      label: 'Private contract',
      type: 'switch',
      defaultValue: false,
      switchLabel: 'Visible only to you (not shared with your department)',
      labelPosition: 'left',
      table: {
        header: 'Private',
        width: '88px',
        sortable: true,
        cellRenderer: 'default',
      },
      filterable: true,
      filterType: 'boolean',
      validation: { required: false },
    },
    {
      name: 'startDate',
      label: 'Start Date',
      type: 'date',
      required: true,
      table: {
        header: 'Start Date',
        width: tableConfig.dateColumnWidth,
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table'),
      },
      filterable: true,
      filterType: 'dateRange',
      validation: {
        required: true,
      },
    },
    {
      name: 'expiryDate',
      label: 'Expiry Date',
      type: 'date',
      required: false,
      table: {
        header: 'Expiry Date',
        width: tableConfig.dateColumnWidth,
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table'),
      },
      filterable: true,
      filterType: 'dateRange',
      validation: {
        required: false,
      },
    },
    {
      name: 'reminderDays',
      label: 'Reminder Days',
      type: 'number',
      required: true,
      min: 0,
      step: '1',
      defaultValue: 30,
      table: { header: 'Reminder (days)', width: '140px', sortable: true },
      filterable: true,
      validation: {
        required: true,
        integer: true,
        min: 0,
        message: 'Reminder days must be a number greater than or equal to 0',
      },
    },
    {
      name: 'contractStatus',
      label: 'Status',
      type: 'text',
      readOnly: true,
      table: {
        header: 'Status',
        width: '140px',
        sortable: true,
        cellRenderer: 'statusBadge',
      },
      filterable: true,
      filterType: 'multiSelect',
    },
    {
      name: 'fileAttachments',
      label: 'Attachments',
      type: 'fileUpload',
      multiple: true,
      accept: '.pdf,.doc,.docx,.xls,.xlsx,.txt,.md,image/*',
      maxSize: 50 * 1024 * 1024,
      maxFiles: 20,
      placeholder: 'Drop contract documents here or click to browse',
      table: {
        header: 'Files',
        width: '56px',
        sortable: false,
        displayField: 'fileAttachmentCount',
        cellRenderer: 'fileCount',
      },
      filterable: false,
      validation: {
        required: false,
      },
    },
    {
      name: 'createdAt',
      label: 'Created',
      type: 'date',
      readOnly: true,
      table: {
        header: 'Created',
        width: tableConfig.dateColumnWidth,
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table'),
      },
      filterable: true,
      filterType: 'dateRange',
    },
    {
      name: 'updatedAt',
      label: 'Updated',
      type: 'date',
      readOnly: true,
      table: {
        header: 'Updated',
        width: tableConfig.dateColumnWidth,
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table'),
      },
      filterable: true,
      filterType: 'dateRange',
    },
  ],

  get tableColumns() {
    return this.fields
      .filter((field) => field.table !== false && field.table)
      .map((field) => ({
        key: field.table.displayField || field.name,
        header: field.table.header || field.label,
        width: field.table.width || 'auto',
        sortable: field.table.sortable !== false,
        displayField: field.table.displayField || field.name,
        cellRenderer: field.table.cellRenderer || 'default',
        originalField: field.name,
      }));
  },

  exportFileName: 'qms_contracts',

  fieldMappings: {
    supplier: 'supplier.name',
    category: 'category.catName',
    userDepartment: 'userDepartment.name',
  },

  get filterableFields() {
    return this.fields.filter((field) => field.filterable === true).map((field) => field.name);
  },

  get validationRules() {
    const rules = {};
    this.fields.forEach((field) => {
      if (field.validation) {
        rules[field.name] = field.validation;
      }
    });
    return rules;
  },

  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: [
        'contractNumber',
        'contractTitle',
        'supplierId',
        'categoryId',
        'departmentId',
        'isPrivate',
        'contractStatus',
      ],
    },
    {
      name: 'dates',
      label: 'Dates',
      icon: '📅',
      fields: ['startDate', 'expiryDate', 'reminderDays'],
    },
  ],

  formSections: [
    {
      title: 'Contract Information',
      icon: '📄',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: ['contractNumber', 'contractTitle', 'description'],
      fieldSpans: {
        contractNumber: 'md:col-span-4',
        contractTitle: 'md:col-span-8',
        description: 'md:col-span-12',
      },
    },
    {
      title: 'Classification',
      icon: '🏷️',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: ['supplierId', 'categoryId', 'departmentId', 'isPrivate'],
      fieldSpans: {
        supplierId: 'md:col-span-6',
        categoryId: 'md:col-span-3',
        departmentId: 'md:col-span-3',
        isPrivate: 'md:col-span-12',
      },
    },
    {
      title: 'Dates & Reminder',
      icon: '⏰',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: ['startDate', 'expiryDate', 'reminderDays'],
      fieldSpans: {
        startDate: 'md:col-span-4',
        expiryDate: 'md:col-span-4',
        reminderDays: 'md:col-span-4',
      },
    },
    {
      title: 'Attachments',
      icon: '📎',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: ['fileAttachments'],
      fieldSpans: {
        fileAttachments: 'md:col-span-12',
      },
    },
  ],

  trackingSection: createTrackingConfig('Contract'),

  labels: {
    singular: 'Contract',
    plural: 'Contracts',
    pageTitle: 'Contract Management',
    pageSubtitle: 'Manage supplier contracts and monitor expirations',
    searchPlaceholder: 'Search contracts by number, title, entity, or category...',
    viewTitle: 'View Contract',
    noSelectionText: 'Select a contract to see details',
  },

  transformData: (data) => {
    const transformed = { ...data };

    delete transformed.createdBy;
    delete transformed.updatedBy;
    delete transformed.createdAt;
    delete transformed.updatedAt;

    ['supplierId', 'categoryId', 'departmentId', 'reminderDays'].forEach((field) => {
      if (transformed[field] !== undefined) {
        if (transformed[field] === '' || transformed[field] === null) {
          transformed[field] = null;
        } else {
          const value = parseInt(transformed[field], 10);
          transformed[field] = Number.isNaN(value) ? null : value;
        }
      }
    });

    ['startDate', 'expiryDate'].forEach((field) => {
      if (transformed[field] !== undefined) {
        if (transformed[field] === '' || transformed[field] === null) {
          transformed[field] = null;
        } else {
          const dateValue = new Date(transformed[field]);
          transformed[field] = Number.isNaN(dateValue.getTime()) ? null : dateValue.toISOString();
        }
      }
    });

    if (transformed.description === '') {
      transformed.description = null;
    }

    if (transformed.isPrivate !== undefined && transformed.isPrivate !== null) {
      transformed.isPrivate = Boolean(transformed.isPrivate);
    } else {
      transformed.isPrivate = false;
    }

    delete transformed.contractStatus;
    delete transformed.fileAttachments;
    delete transformed.fileAttachmentCount;

    return transformed;
  },

  messages: {
    loading: 'Loading contracts...',
    noData: 'No contracts found. Create your first contract to get started.',
    loadError: 'Failed to load contracts',
    saveSuccess: 'Contract saved successfully!',
    saveError: 'Failed to save contract',
    deleteSuccess: 'Contract deleted successfully!',
    deleteError: 'Failed to delete contract',
    deleteConfirm: 'Are you sure you want to delete this contract?',
  },

  validateField: null,

  helpText: {
    contractNumber: { short: 'Unique identifier used to track this contract across systems.' },
    contractTitle: { short: 'Human-readable title describing the agreement.' },
    supplierId: { short: 'Entity (e.g. company or organization) linked to this contract.' },
    categoryId: { short: 'Contract category used for grouping and reporting.' },
    departmentId: { short: 'User department linked to this contract.' },
    isPrivate: {
      short:
        'When on, only you can see this contract; it is hidden from others in your department list.',
    },
    contractStatus: { short: 'Lifecycle state derived from contract dates and reminder window.' },
    startDate: { short: 'Contract effective start date.' },
    expiryDate: { short: 'Contract expiration date used for alerting and renewals.' },
    reminderDays: { short: 'Number of days before expiry to trigger reminders.' },
    fileAttachments: {
      short: 'Signed agreements, amendments, scans, or other supporting documents.',
    },
  },
};

export default contractConfig;
